import React, { useCallback, useEffect, useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  Pagination,
  InstantSearch,
  Hits,
  Configure,
  SearchBox,
  useInstantSearch,
} from 'react-instantsearch-hooks-web';
import { Hit } from './Hit';
import { SearchIcon } from '../Icons';
import { LogoMark } from '../Logo';
import './SearchResults.styles.scss';

const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID as string,
  process.env.GATSBY_ALGOLIA_API_KEY as string
);

interface SearchResultsProps {
  show: boolean;
  set: (b: boolean) => void;
}

export const SearchResults = ({ show, set }: SearchResultsProps) => {
  const [mounted, setMounted] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.code) {
        case 'Slash':
          set(!show);
          break;
        case 'KeyK':
          if (e.ctrlKey || e.metaKey) {
            set(!show);
          }
          break;
        case 'Escape':
          if (show) {
            set(false);
          }
          break;
        default:
          return;
      }
    },
    [set, show]
  );

  /**
   * Listen for cmd + K, ctr + K, and escape keys
   */
  useEffect(() => {
    window.addEventListener(`keydown`, handleKeyDown);
    return () => window.removeEventListener(`keydown`, handleKeyDown);
  }, [handleKeyDown]);

  /**
   * Track when the search results component should mount and unmount
   */
  useEffect(() => {
    setMounted(show);
  }, [show]);

  /**
   * Update scroll and focus
   */
  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = 'hidden';
      const input = document.querySelector('#algolia-search input') as
        | HTMLInputElement
        | undefined;
      input?.focus();
    } else {
      if (document.body.style.overflow === 'hidden') {
        document.getElementById('search-btn')?.focus();
      }
      document.body.style.removeProperty('overflow');
    }
  }, [mounted]);

  return show ? (
    <div id="search-modal">
      <button className="overlay" onClick={() => set(false)}>
        <span className="close-label">Close</span>
      </button>
      <InstantSearch
        searchClient={searchClient}
        indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME as string}>
        <Configure hitsPerPage={10} attributesToSnippet={['content']} />
        <div className="search-box">
          <div className="search-header">
            <SearchIcon />
            <SearchBox
              id="algolia-search"
              placeholder="Search Phenix documentation..."
              resetIconComponent={() => <>Clear</>}
            />
          </div>
          <div className="search-body">
            <NoResultBoundary>
              <Hits
                hitComponent={(props) => (
                  <Hit dismiss={() => set(false)} {...props} />
                )}
              />
            </NoResultBoundary>
          </div>
          <div className="search-footer flex align-center justify-between">
            <ResultPagination />
            <a
              href="https://algolia.com"
              target="_blank"
              rel="noopener noreferrer"></a>
          </div>
        </div>
      </InstantSearch>
    </div>
  ) : null;
};

const ResultPagination = () => {
  const { results, indexUiState } = useInstantSearch();
  const noQuery = !indexUiState.query;

  return (
    <div className="result-pagination">
      {!noQuery && results.nbPages > 1 ? (
        <Pagination
          showFirst={false}
          showLast={false}
          classNames={{
            root: 'search-page-wrapper',
            link: 'page-link',
            selectedItem: 'active',
            disabledItem: 'disabled',
            list: 'page-list',
          }}
        />
      ) : null}
    </div>
  );
};

const NoResultBoundary = ({ children }: { children: React.ReactNode }) => {
  const { indexUiState, results } = useInstantSearch();
  if (!indexUiState.query) {
    return (
      <div className="no-query cover">
        <div className="bg-logo cover">
          <LogoMark />
        </div>
        <span>Type a phrase or keyword to search our docs</span>
      </div>
    );
  }

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        <div className="no-results flex align-center justify-center">
          No results
        </div>
        <div hidden>{children}</div>
      </>
    );
  }

  return <>{children}</>;
};
